import { artScale, uiScaleOf } from "odyssey-shared";
import type { ArtBox } from "../ui/kit/ArtIcon.tsx";
import type { DeviceView } from "./viewport.ts";

/**
 * How the UI scale reaches the DOM (ADR-061, "the scale is computed in device pixels and reaches the DOM
 * as `--ui-scale` and `--px`"). The overlay root carries the custom properties and nothing else sets
 * them: no media query, no stylesheet. Every DOM size is authored as `calc(N * var(--px))` with a whole N,
 * so one logical pixel is always a whole number of device pixels. Colour art (ADR-111 decision 12) has a
 * length of its own per box, `--art-48`, `--art-32`, `--art-22`, `--art-16`, `--art-13`, `--art-10` and `--art-8`: one art pixel, k = `artScale(box, d)` device pixels,
 * so the art stays on whole device pixels whatever the ratio.
 */

/**
 * The overlay root's custom properties.
 */
export interface OverlayProperties {
  readonly "--ui-scale": string;
  readonly "--px": string;
  readonly "--art-48": string;
  readonly "--art-32": string;
  readonly "--art-22": string;
  readonly "--art-16": string;
  readonly "--art-13": string;
  readonly "--art-10": string;
  readonly "--art-8": string;
}

/**
 * One art pixel of a box as a CSS length.
 *
 * @param box The box.
 * @param scale The UI scale d.
 * @param ratio The view's device pixel ratio.
 * @returns k device pixels in CSS pixels, `0px` where the art does not fit.
 */
function artProperty(box: ArtBox, scale: number, ratio: number): string {
  return `${artScale(box, scale) / ratio}px`;
}

/**
 * The overlay root's custom properties for a view.
 *
 * @param view The window in device pixels and its ratio.
 * @returns `--ui-scale`, the whole-number UI scale, `--px`, that many device pixels expressed in CSS
 * pixels (`--ui-scale` / `devicePixelRatio`), and `--art-48`, `--art-32`, `--art-22`, `--art-16`, `--art-13`, `--art-10` and `--art-8`, one art pixel of
 * each box the same way.
 * @example
 * overlayProperties({ width: 1920, height: 1080, ratio: 1 });
 * // { "--ui-scale": "3", "--px": "3px", "--art-48": "4px", "--art-32": "3px", "--art-22": "2px", "--art-16": "1px", "--art-13": "0px", "--art-10": "0px", "--art-8": "0px" }
 */
export function overlayProperties(view: DeviceView): OverlayProperties {
  const scale = uiScaleOf(view);
  return { "--ui-scale": String(scale), "--px": `${scale / view.ratio}px`, "--art-48": artProperty(48, scale, view.ratio), "--art-32": artProperty(32, scale, view.ratio), "--art-22": artProperty(22, scale, view.ratio), "--art-16": artProperty(16, scale, view.ratio), "--art-13": artProperty(13, scale, view.ratio), "--art-10": artProperty(10, scale, view.ratio), "--art-8": artProperty(8, scale, view.ratio) };
}

/**
 * Sets the overlay root's custom properties for a view.
 *
 * @param root The overlay root.
 * @param view The window in device pixels and its ratio.
 */
export function applyOverlayScale(root: HTMLElement, view: DeviceView): void {
  const properties = overlayProperties(view);
  root.style.setProperty("--ui-scale", properties["--ui-scale"]);
  root.style.setProperty("--px", properties["--px"]);
  root.style.setProperty("--art-48", properties["--art-48"]);
  root.style.setProperty("--art-32", properties["--art-32"]);
  root.style.setProperty("--art-22", properties["--art-22"]);
  root.style.setProperty("--art-16", properties["--art-16"]);
  root.style.setProperty("--art-13", properties["--art-13"]);
  root.style.setProperty("--art-10", properties["--art-10"]);
  root.style.setProperty("--art-8", properties["--art-8"]);
}
